import React from 'react';
import { formatClockMinutes, money, parseAmt, parseClock, parseDur } from './clockParse';
import { formatHours } from '../../util/hoursFormat';

/**
 * Payroll editor (M2-B) — the modal echo line's parse feedback, the design
 * bundle's shape: `730 → 7:30a · 1500 → 3:00p · duration 7:30`, with a red `?`
 * on a parse failure. Duration shown in the tenant's hours format.
 *
 * The echo is a safety net for what the 10-key parser *did* with your digits,
 * so it only speaks when it has something to say: an empty field echoes
 * nothing (no `— → ?` noise), and a field that already reads as its resolved
 * value — every prefilled field when editing an existing entry — is not echoed
 * back at itself.
 */

/** `raw → resolved`, unless raw is blank or already says exactly that. */
const Echo: React.FC<{ raw: string; resolved: string | null; label?: string }> = ({ raw, resolved, label }) => {
  const text = raw.trim();
  if (text === '') return null;
  if (resolved != null && resolved === text) return null;
  return (
    <span>
      {label ? `${label} ` : ''}<b>{raw}</b> → {resolved == null ? <span className="warnx">?</span> : resolved}
    </span>
  );
};

export const PunchEchoParts: React.FC<{ inRaw: string; outRaw: string }> = ({ inRaw, outRaw }) => {
  const i = parseClock(inRaw);
  const o = outRaw.trim() === '' ? undefined : parseClock(outRaw);
  const durMin = i != null && o != null ? (o >= i ? o - i : o + 1440 - i) : null;
  return (
    <>
      <Echo raw={inRaw} resolved={i == null ? null : formatClockMinutes(i)} />
      <Echo raw={outRaw} resolved={o == null ? null : formatClockMinutes(o)} />
      {durMin != null && <span>duration <b>{formatHours(durMin)}</b></span>}
    </>
  );
};

export const DurEchoParts: React.FC<{ raw: string; label?: string }> = ({ raw, label }) => {
  const m = parseDur(raw);
  return <Echo raw={raw} resolved={m == null ? null : formatHours(m)} label={label} />;
};

export const AmountEchoParts: React.FC<{ raw: string; label?: string }> = ({ raw, label = 'amount' }) => {
  const a = parseAmt(raw);
  return <Echo raw={raw} resolved={a == null ? null : money(a)} label={label} />;
};
